/**
 * Room Mockups - Canvas-based product image overlay on room scenes
 *
 * Uses actual print size dimensions (inches) to proportionally place
 * the product image within the room mockup canvas area.
 * Re-renders when print size changes.
 */
(function () {
  var configEl = document.querySelector('[data-frame-options-config]');
  if (!configEl) return;

  var config = JSON.parse(configEl.textContent);
  var mockups = (config.roomMockups || []).filter(Boolean);
  var productImageUrl = config.productImage;

  if (mockups.length === 0 || !productImageUrl) return;

  if (productImageUrl.indexOf('//') === 0) productImageUrl = 'https:' + productImageUrl;

  var mainSlider = document.querySelector('.js-carousel-main');
  var thumbWrapper = document.querySelector('.js-thumb-carousel-nav');
  if (!mainSlider) return;

  var printSizeSelect = document.querySelector('[data-field="print-size"]');

  // Cached loaded images
  var cachedProductImg = null;
  var cachedRoomImages = {}; // url -> Image

  // Track generated elements
  var generatedSlides = [];
  var generatedThumbs = [];

  function parseDimensions(sizeStr) {
    if (!sizeStr) return { width: 0, height: 0 };
    var cleaned = sizeStr.replace(/cm|in|"|'/gi, '').trim();
    var parts = cleaned.split(/\s*[xX×]\s*/);
    return { width: parseFloat(parts[0]) || 0, height: parseFloat(parts[1]) || 0 };
  }

  function getCurrentSize() {
    if (printSizeSelect) return parseDimensions(printSizeSelect.value);
    // Fallback: use first variant
    if (config.variants && config.variants.length > 0) {
      return parseDimensions(config.variants[0].option1);
    }
    return { width: 0, height: 0 };
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('Failed to load: ' + url)); };
      img.src = url;
    });
  }

  // Current frame-options state
  var currentDetail = { type: 'art-print', printSize: { width: 0, height: 0 }, frameData: null, matEnabled: false };

  function createMockupCanvas(roomImg, productImg, mockup, printSize, detail) {
    var canvas = document.createElement('canvas');
    canvas.width = roomImg.naturalWidth;
    canvas.height = roomImg.naturalHeight;
    var ctx = canvas.getContext('2d');

    // Draw room background
    ctx.drawImage(roomImg, 0, 0, canvas.width, canvas.height);

    // Canvas area position/size in pixels
    var areaX = (mockup.canvas_x / 100) * canvas.width;
    var areaY = (mockup.canvas_y / 100) * canvas.height;
    var areaW = (mockup.canvas_width / 100) * canvas.width;
    var areaH = (mockup.canvas_height / 100) * canvas.height;

    // Real wall dimensions of the canvas area (inches)
    var wallW = mockup.canvas_real_width_inch || 0;
    var wallH = mockup.canvas_real_height_inch || 0;

    // Print size in inches — orient based on product image aspect ratio
    var imgIsLandscape = productImg.naturalWidth > productImg.naturalHeight;
    var sizeA = printSize.width;
    var sizeB = printSize.height;
    var sizeW, sizeH;
    if (imgIsLandscape) {
      sizeW = Math.max(sizeA, sizeB);
      sizeH = Math.min(sizeA, sizeB);
    } else {
      sizeW = Math.min(sizeA, sizeB);
      sizeH = Math.max(sizeA, sizeB);
    }
    if (sizeW <= 0 || sizeH <= 0 || wallW <= 0 || wallH <= 0) {
      sizeW = productImg.naturalWidth;
      sizeH = productImg.naturalHeight;
      wallW = sizeW;
      wallH = sizeH;
    }

    var ppiX = areaW / wallW;
    var ppiY = areaH / wallH;
    var ppi = Math.min(ppiX, ppiY);

    var drawW = sizeW * ppi;
    var drawH = sizeH * ppi;

    if (drawW > areaW || drawH > areaH) {
      var scale = Math.min(areaW / drawW, areaH / drawH);
      drawW *= scale;
      drawH *= scale;
    }

    // Determine frame/mat dimensions in pixels
    var type = detail ? detail.type : 'art-print';
    var canvasWrapOn = !!(detail && detail.canvasWrap);
    var frameThick = 0;
    var matThick = 0;
    var canvasDepth = 0;

    if (type === 'framed-print') {
      frameThick = Math.round(Math.min(drawW, drawH) * 0.06);
      if (detail && detail.matEnabled) {
        matThick = Math.round(Math.min(drawW, drawH) * 0.05);
      }
    } else if (type === 'canvas-print' && canvasWrapOn) {
      canvasDepth = Math.round(Math.min(drawW, drawH) * 0.04);
    }

    // Total outer dimensions including frame/mat
    var outerW = drawW + (frameThick + matThick) * 2;
    var outerH = drawH + (frameThick + matThick) * 2;

    // Clamp outer to area
    if (outerW > areaW || outerH > areaH) {
      var s2 = Math.min(areaW / outerW, areaH / outerH);
      outerW *= s2;
      outerH *= s2;
      drawW *= s2;
      drawH *= s2;
      frameThick = Math.round(frameThick * s2);
      matThick = Math.round(matThick * s2);
      canvasDepth = Math.round(canvasDepth * s2);
    }

    // Center the whole assembly
    var outerX = areaX + (areaW - outerW) / 2;
    var outerY = areaY + (areaH - outerH) / 2;
    var imgX = outerX + frameThick + matThick;
    var imgY = outerY + frameThick + matThick;

    // Clip to area
    ctx.save();
    ctx.beginPath();
    ctx.rect(areaX, areaY, areaW, areaH);
    ctx.clip();

    // Shadow
    ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetX = 3;
    ctx.shadowOffsetY = 5;

    if (type === 'framed-print' && frameThick > 0) {
      // Draw frame rectangle (shadow applies to frame)
      var frameColor = '#444';
      if (detail && detail.frameData && detail.frameData.label) {
        var lbl = detail.frameData.label.toLowerCase();
        if (lbl === 'white') frameColor = '#e8e8e8';
        else if (lbl === 'black') frameColor = '#222';
        else if (lbl === 'natural') frameColor = '#c9a96e';
        else if (lbl === 'dark') frameColor = '#3a2a1a';
      }
      ctx.fillStyle = frameColor;
      ctx.fillRect(outerX, outerY, outerW, outerH);

      // Turn off shadow for inner elements
      ctx.shadowColor = 'transparent';

      // Draw mat
      if (matThick > 0) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(outerX + frameThick, outerY + frameThick,
          outerW - frameThick * 2, outerH - frameThick * 2);
      }

      // Draw product image
      ctx.drawImage(productImg, imgX, imgY, drawW, drawH);
    } else if (type === 'canvas-print' && canvasWrapOn) {
      // Draw product image (shadow applies)
      ctx.drawImage(productImg, imgX, imgY, drawW, drawH);
      ctx.shadowColor = 'transparent';

      // Canvas depth effect — right side
      if (canvasDepth > 0) {
        ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
        ctx.fillRect(imgX + drawW, imgY + canvasDepth, canvasDepth, drawH);
        // Bottom side
        ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.fillRect(imgX + canvasDepth, imgY + drawH, drawW, canvasDepth);
      }
    } else {
      // Art print — just the image
      ctx.drawImage(productImg, imgX, imgY, drawW, drawH);
      ctx.shadowColor = 'transparent';
    }

    // Thin border
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.08)';
    ctx.lineWidth = 1;
    if (type === 'framed-print' && frameThick > 0) {
      ctx.strokeRect(outerX, outerY, outerW, outerH);
    } else {
      ctx.strokeRect(imgX, imgY, drawW, drawH);
    }

    ctx.restore();

    return canvas;
  }

  function createSlideElement(canvas, mockup, index) {
    var slideDiv = document.createElement('div');
    slideDiv.className = 'image-slide carousel-cell';
    slideDiv.setAttribute('data-mockup-handle', mockup.handle);
    slideDiv.tabIndex = -1;

    var mediaContainer = document.createElement('div');
    mediaContainer.className = 'product-media-container image';
    mediaContainer.setAttribute('data-product-single-media-wrapper', '');

    var boxRatio = document.createElement('div');
    boxRatio.className = 'box-ratio';
    var ratio = (canvas.height / canvas.width * 100).toFixed(2);
    boxRatio.style.paddingBottom = ratio + '%';

    var img = document.createElement('img');
    img.src = canvas.toDataURL('image/jpeg', 0.9);
    img.className = 'product__image';
    img.alt = mockup.label || 'Room mockup';
    img.loading = 'lazy';
    img.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:fill;';

    boxRatio.appendChild(img);
    mediaContainer.appendChild(boxRatio);
    slideDiv.appendChild(mediaContainer);

    return slideDiv;
  }

  function createThumbElement(canvas, mockup) {
    var thumbDiv = document.createElement('div');
    thumbDiv.className = 'image-thumbnail product-single__thumbnails-item carousel-cell js-thumb-item';
    thumbDiv.setAttribute('data-mockup-handle', mockup.handle);
    thumbDiv.tabIndex = 0;

    var innerDiv = document.createElement('div');
    innerDiv.className = 'product-single__thumbnail js-thumb-item-img-wrap';

    var boxRatio = document.createElement('div');
    boxRatio.className = 'box-ratio';
    var ratio = (canvas.height / canvas.width * 100).toFixed(2);
    boxRatio.style.paddingBottom = ratio + '%';

    var img = document.createElement('img');
    img.src = canvas.toDataURL('image/jpeg', 0.85);
    img.className = 'js-thumb-item-img';
    img.alt = mockup.label || 'Room mockup';
    img.loading = 'lazy';
    img.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:cover;';

    boxRatio.appendChild(img);
    innerDiv.appendChild(boxRatio);
    thumbDiv.appendChild(innerDiv);

    thumbDiv.addEventListener('click', function () {
      navigateToSlide(thumbDiv);
    });

    return thumbDiv;
  }

  function navigateToSlide(thumbEl) {
    // Find index of this thumb among all thumbs
    if (!thumbWrapper) return;
    var allThumbs = Array.from(thumbWrapper.querySelectorAll('.js-thumb-item'));
    var idx = allThumbs.indexOf(thumbEl);
    if (idx < 0) return;

    var flickity = window.Flickity && Flickity.data(mainSlider);
    if (flickity) {
      flickity.select(idx);
    }

    // Update thumb selection
    allThumbs.forEach(function (t) { t.classList.remove('is-nav-selected'); });
    thumbEl.classList.add('is-nav-selected');
  }

  function removeGeneratedElements() {
    var flickity = window.Flickity && Flickity.data(mainSlider);

    generatedSlides.forEach(function (el) {
      if (flickity) {
        flickity.remove(el);
      } else {
        el.remove();
      }
    });
    generatedThumbs.forEach(function (el) { el.remove(); });
    generatedSlides = [];
    generatedThumbs = [];
  }

  function removeExtraProductSlides() {
    var flickity = window.Flickity && Flickity.data(mainSlider);

    // Remove product media slides except first
    var existingSlides = Array.from(mainSlider.querySelectorAll('.carousel-cell:not([data-mockup-handle])'));
    for (var i = existingSlides.length - 1; i > 0; i--) {
      if (flickity) {
        flickity.remove(existingSlides[i]);
      } else {
        existingSlides[i].remove();
      }
    }

    // Remove thumbnails except first
    if (thumbWrapper) {
      var existingThumbs = thumbWrapper.querySelectorAll('.js-thumb-item:not([data-mockup-handle])');
      for (var j = existingThumbs.length - 1; j > 0; j--) {
        existingThumbs[j].remove();
      }
    }
  }

  function renderMockups(printSize) {
    removeGeneratedElements();
    removeExtraProductSlides();

    var flickity = window.Flickity && Flickity.data(mainSlider);

    // Load product image (use cache)
    var productPromise = cachedProductImg
      ? Promise.resolve(cachedProductImg)
      : loadImage(productImageUrl).then(function (img) { cachedProductImg = img; return img; });

    productPromise.then(function (productImg) {
      var promises = mockups.map(function (mockup) {
        var roomUrl = mockup.image;
        if (roomUrl && roomUrl.indexOf('//') === 0) roomUrl = 'https:' + roomUrl;
        if (!roomUrl) return Promise.resolve(null);

        if (cachedRoomImages[roomUrl]) {
          return Promise.resolve({ roomImg: cachedRoomImages[roomUrl], mockup: mockup });
        }

        return loadImage(roomUrl).then(function (roomImg) {
          cachedRoomImages[roomUrl] = roomImg;
          return { roomImg: roomImg, mockup: mockup };
        }).catch(function () { return null; });
      });

      Promise.all(promises).then(function (results) {
        results.forEach(function (result, idx) {
          if (!result) return;

          var canvas = createMockupCanvas(result.roomImg, productImg, result.mockup, printSize, currentDetail);

          var slide = createSlideElement(canvas, result.mockup, idx);
          generatedSlides.push(slide);

          if (flickity) {
            flickity.append(slide);
          } else {
            mainSlider.appendChild(slide);
          }

          if (thumbWrapper) {
            var thumb = createThumbElement(canvas, result.mockup);
            thumbWrapper.appendChild(thumb);
            generatedThumbs.push(thumb);
          }
        });

        if (flickity) flickity.resize();

        if (thumbWrapper && window.Flickity) {
          var thumbFlickity = Flickity.data(thumbWrapper);
          if (thumbFlickity) {
            thumbFlickity.reloadCells();
            thumbFlickity.resize();
          }
        }

        revealGallery();
      });
    }).catch(function (err) {
      revealGallery();
    });
  }

  function revealGallery() {
    var gallery = document.querySelector('.frame-preview-pending');
    if (gallery) {
      gallery.classList.add('preview-ready');
    } else {
    }
  }

  // --- Update canvas images in-place when size changes ---
  function updateMockupImages(printSize) {
    if (!cachedProductImg) return;

    generatedSlides.forEach(function (slide, idx) {
      var mockup = mockups[idx];
      if (!mockup) return;

      var roomUrl = mockup.image;
      if (roomUrl && roomUrl.indexOf('//') === 0) roomUrl = 'https:' + roomUrl;
      var roomImg = cachedRoomImages[roomUrl];
      if (!roomImg) return;

      var canvas = createMockupCanvas(roomImg, cachedProductImg, mockup, printSize, currentDetail);

      // Update main slide img src
      var slideImg = slide.querySelector('.product__image');
      if (slideImg) slideImg.src = canvas.toDataURL('image/jpeg', 0.9);

      // Update thumbnail img src
      var thumb = generatedThumbs[idx];
      if (thumb) {
        var thumbImg = thumb.querySelector('.js-thumb-item-img');
        if (thumbImg) thumbImg.src = canvas.toDataURL('image/jpeg', 0.85);
      }
    });
  }

  // --- Listen for frame-options changes (size, type, frame, mat) ---
  document.addEventListener('frame-options:change', function (e) {
    var detail = e.detail;
    if (!detail) return;
    currentDetail = detail;
    var newSize = detail.printSize;
    if (newSize && newSize.width > 0 && newSize.height > 0) {
      updateMockupImages(newSize);
    }
  });

  // --- Init — wait for product preview to be ready first ---
  function init() {
    var printSize = getCurrentSize();
    var flickity = window.Flickity && Flickity.data(mainSlider);
    if (flickity) {
      renderMockups(printSize);
    } else {
      setTimeout(function () {
        renderMockups(printSize);
      }, 1000);
    }
  }

  // Wait for product-preview to finish rendering before adding mockups
  document.addEventListener('product-preview:ready', function () {
    init();
  }, { once: true });

  // Fallback: if preview never fires (e.g. no config), init after load
  window.addEventListener('load', function () {
    setTimeout(function () {
      if (generatedSlides.length === 0) {
        init();
      }
    }, 2000);
  });
})();
